import { DeleteOutlined, Error } from "@mui/icons-material";
import CheckIcon from "@mui/icons-material/Check";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Checkbox,
  Grid2,
  IconButton,
} from "@mui/material";
import { useTheme } from "@mui/material";
import grey from "@mui/material/colors/grey";
import { PimoReactComponent } from "@pimo/pimo-app-builder";
import { ControlledDateFormField } from "@pimo/pimo-components/src/lib/pimo-components/pimo-form/controlled-fields/controlled-date-field";
import { ControlledRichTextField } from "@pimo/pimo-components/src/lib/pimo-components/pimo-form/controlled-fields/controlled-rich-text-field";
import { ControlledSelectField } from "@pimo/pimo-components/src/lib/pimo-components/pimo-form/controlled-fields/controlled-select-field";
import { ControlledTextFormField } from "@pimo/pimo-components/src/lib/pimo-components/pimo-form/controlled-fields/controlled-text-field";
import { FormFieldWrapper } from "@pimo/pimo-components/src/lib/pimo-components/pimo-form/form-field-wrapper";
import { getLocalizedMessage } from "@pimo/pimo-l10n";
import dayjs from "dayjs";
import React from "react";
import { Configuration, ExtendedFieldError, Project } from "vmd-types";

import { getDateInputFormat } from "../../utils/format-date";

type ProjectRowProps = {
  field: Project;
  formState: {
    projects?: Project[];
    configuration?: Configuration;
    validationErrors: Record<string, ExtendedFieldError>;
  };
  setValue: (key: string, value: unknown) => void;
  handleDelete: (ids: string[]) => void;
  selectedProjectIndices: string[];
  setSelectedProjectIndices: React.Dispatch<React.SetStateAction<string[]>>;
  projectName: { single: string; plural: string };
  onFieldChange?: (id: string, fieldKey: string, value: unknown) => void;
  language?: string;
};

export const ProjectRow: PimoReactComponent<ProjectRowProps> = ({
  field,
  formState,
  setValue,
  handleDelete,
  selectedProjectIndices,
  setSelectedProjectIndices,
  projectName,
  onFieldChange,
  language = "en",
}) => {
  const theme = useTheme();
  const getFieldIndexUsingId = (fieldId: string) => {
    return formState.projects?.findIndex((p) => p.id === fieldId);
  };
  const fieldIndex = getFieldIndexUsingId(field.id);

  const getFieldDefinition = (fieldName: string) => {
    return formState.configuration?.fieldsDefinition
      ?.filter((field) => field.purpose === "projectBasic")
      ?.find((f) => f.fieldName === fieldName && f.enabled === true);
  };
  return (
    <Accordion
      key={field.id}
      slotProps={{ transition: { unmountOnExit: true, mountOnEnter: true } }}
      sx={{
        boxShadow: "none",
        contentVisibility: "auto",
        containIntrinsicSize: "auto 56px",
      }}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        aria-controls="panel1-content"
        id="panel1-header"
        key={field.id}
      >
        <Grid2
          container
          alignItems="center"
          sx={{ width: "100%" }}
          key={field.id}
        >
          <Grid2 size={0.5}>
            <Checkbox
              onClick={(e) => {
                e.stopPropagation();
              }}
              onChange={(e) => {
                const isChecked = e.target.checked;
                setSelectedProjectIndices((prev) =>
                  isChecked
                    ? [...prev, field.id ?? 0]
                    : prev.filter((i) => i !== field.id)
                );
              }}
              icon={
                <span
                  style={{
                    width: 20,
                    height: 20,
                    border: "2px solid",
                    borderColor: grey[700],

                    display: "inline-block",
                    backgroundColor: "#fff",
                  }}
                />
              }
              checkedIcon={
                <span
                  style={{
                    width: 20,
                    height: 20,

                    border: "2px solid",
                    borderColor: grey[700],
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    backgroundColor: "#fff",
                  }}
                >
                  <CheckIcon
                    style={{
                      fontSize: 18,
                      color: grey[700],
                    }}
                  />
                </span>
              }
              checked={(selectedProjectIndices ?? [])?.includes(field.id ?? 0)}
            />
          </Grid2>
          <Grid2 sx={{ fontSize: "14px" }} size={10.5}>
            {formState?.projects?.find((item) => item.id === field.id)?.name}
          </Grid2>
          <Grid2 sx={{ fontSize: "14px" }} size={0.5}>
            <IconButton
              size="small"
              color="primary"
              onClick={() => handleDelete([field.id])}
            >
              <DeleteOutlined />
            </IconButton>
          </Grid2>
          {Object.keys(formState?.validationErrors ?? {})?.find((item) =>
            item.startsWith(`projects.${fieldIndex}`)
          ) && (
            <Grid2
              sx={{
                fontSize: "14px",
                color: theme.palette.error.main,
              }}
              size={0.5}
            >
              <Error />
            </Grid2>
          )}
        </Grid2>
      </AccordionSummary>
      <AccordionDetails key={field.id}>
        <Box
          sx={{
            flexGrow: 1,
            border: "1px solid",
            borderColor: grey[300],
            p: 2,
            borderRadius: "12px",
          }}
        >
          <Grid2 container alignItems="center" spacing={2}>
            {getFieldDefinition("name") && (
              <Grid2 size={6}>
                <FormFieldWrapper
                  fieldWrapperSx={{ fontSize: "14px" }}
                  label={`${getLocalizedMessage(language, "name") || "Name"}${
                    getFieldDefinition("name")?.isMandatory ? "*" : ""
                  }`}
                >
                  <ControlledTextFormField
                    value={field.name ?? ""}
                    onChange={(val) => {
                      if (onFieldChange) {
                        onFieldChange(field.id, "name", val);
                        return;
                      }
                      const index = getFieldIndexUsingId(field.id);
                      setValue(`projects.${index}.name`, val);
                      const newErrors = {
                        ...(formState.validationErrors ?? {}),
                      } as Record<string, ExtendedFieldError>;

                      if (!val && getFieldDefinition("name")?.isMandatory) {
                        newErrors[`projects.${index}.name`] = {
                          type: "required",
                          message: "This field is required",
                          tabContainingErrors: `Manage ${projectName.plural}`,
                        } as ExtendedFieldError;
                      } else {
                        delete newErrors[`projects.${index}.name`];
                      }

                      setValue("validationErrors", newErrors);
                    }}
                    error={
                      formState.validationErrors?.[
                        `projects.${fieldIndex}.name`
                      ] !== undefined
                    }
                    helperText={
                      formState.validationErrors?.[
                        `projects.${fieldIndex}.name`
                      ]?.message
                    }
                  />
                </FormFieldWrapper>
              </Grid2>
            )}

            {getFieldDefinition("customId") && (
              <Grid2 size={6}>
                <FormFieldWrapper
                  label={`${
                    getLocalizedMessage(language, "customId") || "Custom ID"
                  }${getFieldDefinition("customId")?.isMandatory ? "*" : ""}`}
                >
                  <ControlledTextFormField
                    value={field.customId ?? ""}
                    onChange={(val) => {
                      if (onFieldChange) {
                        onFieldChange(field.id, "customId", val);
                        return;
                      }
                      const index = getFieldIndexUsingId(field.id);
                      setValue(`projects.${index}.customId`, val);

                      const newErrors = {
                        ...(formState.validationErrors ?? {}),
                      };
                      if (!val && getFieldDefinition("customId")?.isMandatory) {
                        newErrors[`projects.${index}.customId`] = {
                          type: "required",
                          message: "This field is required",
                          tabContainingErrors: `Manage ${projectName.plural}`,
                        } as ExtendedFieldError;
                      } else {
                        delete newErrors[`projects.${index}.customId`];
                      }

                      setValue("validationErrors", newErrors);
                    }}
                    error={
                      formState.validationErrors?.[
                        `projects.${fieldIndex}.customId`
                      ] !== undefined
                    }
                    helperText={
                      formState.validationErrors?.[
                        `projects.${fieldIndex}.customId`
                      ]?.message
                    }
                  />
                </FormFieldWrapper>
              </Grid2>
            )}

            {getFieldDefinition("description") && (
              <Grid2 size={12}>
                <FormFieldWrapper
                  label={`${
                    getLocalizedMessage(language, "description") ||
                    "Description"
                  }${getFieldDefinition("description")?.isMandatory ? "*" : ""}`}
                >
                  <ControlledRichTextField
                    value={field.description ?? ""}
                    onChange={(val) => {
                      if (onFieldChange) {
                        onFieldChange(field.id, "description", val);
                        return;
                      }
                      const index = getFieldIndexUsingId(field.id);
                      setValue(`projects.${index}.description`, val);
                      const newErrors = {
                        ...(formState.validationErrors ?? {}),
                      } as Record<string, ExtendedFieldError>;
                      if (
                        !val &&
                        getFieldDefinition("description")?.isMandatory
                      ) {
                        newErrors[`projects.${index}.description`] = {
                          type: "required",
                          message: "Description is required",
                          tabContainingErrors: `Manage ${projectName.plural}`,
                        } as ExtendedFieldError;
                      } else {
                        delete newErrors[`projects.${index}.description`];
                      }

                      setValue("validationErrors", newErrors);
                    }}
                    error={
                      formState.validationErrors?.[
                        `projects.${fieldIndex}.description`
                      ] !== undefined
                    }
                    helperText={
                      formState.validationErrors?.[
                        `projects.${fieldIndex}.description`
                      ]?.message
                    }
                  />
                </FormFieldWrapper>
              </Grid2>
            )}
            {getFieldDefinition("owner") && (
              <Grid2 size={6}>
                <FormFieldWrapper
                  label={`${
                    getLocalizedMessage(language, "owner") || "Owner"
                  }${getFieldDefinition("owner")?.isMandatory ? "*" : ""}`}
                >
                  <ControlledTextFormField
                    value={field.owner ?? ""}
                    onChange={(val) => {
                      if (onFieldChange) {
                        onFieldChange(field.id, "owner", val);
                        return;
                      }
                      const index = getFieldIndexUsingId(field.id);
                      setValue(`projects.${index}.owner`, val);
                      const newErrors = {
                        ...(formState.validationErrors ?? {}),
                      } as Record<string, ExtendedFieldError>;
                      if (!val && getFieldDefinition("owner")?.isMandatory) {
                        newErrors[`projects.${index}.owner`] = {
                          type: "required",
                          message: "This field is required",
                          tabContainingErrors: `Manage ${projectName.plural}`,
                        } as ExtendedFieldError;
                      } else {
                        delete newErrors[`projects.${index}.owner`];
                      }

                      setValue("validationErrors", newErrors);
                    }}
                    error={
                      formState.validationErrors?.[
                        `projects.${fieldIndex}.owner`
                      ] !== undefined
                    }
                    helperText={
                      formState.validationErrors?.[
                        `projects.${fieldIndex}.owner`
                      ]?.message
                    }
                  />
                </FormFieldWrapper>
              </Grid2>
            )}
            {getFieldDefinition("approvedBudget") && (
              <Grid2 size={6}>
                <FormFieldWrapper
                  label={`${
                    getFieldDefinition("approvedBudget")?.label ||
                    getLocalizedMessage(language, "budget") ||
                    "Approved Budget"
                  } (€)${
                    getFieldDefinition("approvedBudget")?.isMandatory ? "*" : ""
                  }`}
                >
                  <ControlledTextFormField
                    value={field.approvedBudget?.toString() ?? ""}
                    onChange={(val) => {
                      if (onFieldChange) {
                        onFieldChange(field.id, "approvedBudget", val);
                        return;
                      }
                      const index = getFieldIndexUsingId(field.id);
                      setValue(`projects.${index}.approvedBudget`, val);
                      const newErrors = {
                        ...(formState.validationErrors ?? {}),
                      } as Record<string, ExtendedFieldError>;
                      if (
                        !val &&
                        getFieldDefinition("approvedBudget")?.isMandatory
                      ) {
                        newErrors[`projects.${index}.approvedBudget`] = {
                          type: "required",
                          message: "This field is required",
                          tabContainingErrors: `Manage ${projectName.plural}`,
                        } as ExtendedFieldError;
                      } else {
                        delete newErrors[`projects.${index}.approvedBudget`];
                      }

                      setValue("validationErrors", newErrors);
                    }}
                    isNumber={true}
                    error={
                      formState.validationErrors?.[
                        `projects.${fieldIndex}.approvedBudget`
                      ] !== undefined
                    }
                    helperText={
                      formState.validationErrors?.[
                        `projects.${fieldIndex}.approvedBudget`
                      ]?.message
                    }
                  />
                </FormFieldWrapper>
              </Grid2>
            )}
            {getFieldDefinition("startDate") && (
              <Grid2 size={6}>
                <FormFieldWrapper
                  label={`${
                    getLocalizedMessage(language, "startDate") || "Start Date"
                  }${getFieldDefinition("startDate")?.isMandatory ? "*" : ""}`}
                >
                  <ControlledDateFormField
                    inputFormat={getDateInputFormat(language)}
                    value={field.startDate ?? ""}
                    onChange={(val) => {
                      if (onFieldChange) {
                        onFieldChange(field.id, "startDate", val);
                        return;
                      }
                      const index = getFieldIndexUsingId(field.id);
                      setValue(`projects.${index}.startDate`, val);
                      const newErrors = {
                        ...(formState.validationErrors ?? {}),
                      } as Record<string, ExtendedFieldError>;

                      let entry: ExtendedFieldError | undefined;
                      if (
                        !val &&
                        getFieldDefinition("startDate")?.isMandatory
                      ) {
                        entry = {
                          type: "required",
                          message: "This field is required",
                          tabContainingErrors: `Manage ${projectName.plural}`,
                        };
                      } else if (val && val.isValid() === false) {
                        entry = {
                          type: "invalid",
                          message: "This field is required",
                          tabContainingErrors: `Manage ${projectName.plural}`,
                        };
                      }

                      if (entry) {
                        newErrors[`projects.${index}.startDate`] = entry;
                      } else {
                        delete newErrors[`projects.${index}.startDate`];
                      }

                      setValue("validationErrors", newErrors);
                    }}
                    error={
                      formState.validationErrors?.[
                        `projects.${fieldIndex}.startDate`
                      ] !== undefined
                    }
                    helperText={
                      formState.validationErrors?.[
                        `projects.${fieldIndex}.startDate`
                      ]?.message
                    }
                  ></ControlledDateFormField>
                </FormFieldWrapper>
              </Grid2>
            )}
            {getFieldDefinition("endDate") && (
              <Grid2 size={6}>
                <FormFieldWrapper
                  label={`${
                    getLocalizedMessage(language, "endDate") || "End Date"
                  }${getFieldDefinition("endDate")?.isMandatory ? "*" : ""}`}
                >
                  <ControlledDateFormField
                    inputFormat={getDateInputFormat(language)}
                    value={field.endDate ?? ""}
                    minDate={field.startDate}
                    onChange={(val) => {
                      if (onFieldChange) {
                        onFieldChange(field.id, "endDate", val);
                        return;
                      }
                      const index = getFieldIndexUsingId(field.id);
                      setValue(`projects.${index}.endDate`, val);

                      const newErrors = {
                        ...(formState.validationErrors ?? {}),
                      };

                      let newEntry: ExtendedFieldError | undefined;

                      if (!val && getFieldDefinition("endDate")?.isMandatory) {
                        newEntry = {
                          type: "required",
                          message: "This field is required",
                          tabContainingErrors: `Manage ${projectName.plural}`,
                        };
                      } else if (val && !dayjs(val).isValid()) {
                        newEntry = {
                          type: "invalid",
                          message: "This is not a valid date",
                          tabContainingErrors: `Manage ${projectName.plural}`,
                        };
                      } else if (
                        val &&
                        field.startDate &&
                        val.isBefore(dayjs(field.startDate))
                      ) {
                        newEntry = {
                          type: "invalid",
                          message: "End date should be after start date",
                          tabContainingErrors: `Manage ${projectName.plural}`,
                        };
                      }

                      if (newEntry) {
                        newErrors[`projects.${index}.endDate`] = newEntry;
                      } else {
                        delete newErrors[`projects.${index}.endDate`];
                      }

                      setValue("validationErrors", newErrors);
                    }}
                    error={
                      formState.validationErrors?.[
                        `projects.${fieldIndex}.endDate`
                      ] !== undefined
                    }
                    helperText={
                      formState.validationErrors?.[
                        `projects.${fieldIndex}.endDate`
                      ]?.message
                    }
                  ></ControlledDateFormField>
                </FormFieldWrapper>
              </Grid2>
            )}
            {getFieldDefinition("freeTextFieldOne") && (
              <Grid2 size={6}>
                <FormFieldWrapper
                  label={`${getFieldDefinition("freeTextFieldOne")?.label}${getFieldDefinition("freeTextFieldOne")?.isMandatory ? "*" : ""}`}
                >
                  <ControlledTextFormField
                    value={field.freeTextFieldOne ?? ""}
                    onChange={(val) => {
                      if (onFieldChange) {
                        onFieldChange(field.id, "freeTextFieldOne", val);
                        return;
                      }
                      const index = getFieldIndexUsingId(field.id);
                      setValue(`projects.${index}.freeTextFieldOne`, val);
                      const newErrors = {
                        ...(formState.validationErrors ?? {}),
                      } as Record<string, ExtendedFieldError>;
                      if (
                        !val &&
                        getFieldDefinition("freeTextFieldOne")?.isMandatory
                      ) {
                        newErrors[`projects.${index}.freeTextFieldOne`] = {
                          type: "required",
                          message: "This field is required",
                          tabContainingErrors: `Manage ${projectName.plural}`,
                        } as ExtendedFieldError;
                      } else {
                        delete newErrors[`projects.${index}.freeTextFieldOne`];
                      }

                      setValue("validationErrors", newErrors);
                    }}
                    error={
                      formState.validationErrors?.[
                        `projects.${fieldIndex}.freeTextFieldOne`
                      ] !== undefined
                    }
                    helperText={
                      formState.validationErrors?.[
                        `projects.${fieldIndex}.freeTextFieldOne`
                      ]?.message
                    }
                  />
                </FormFieldWrapper>
              </Grid2>
            )}

            {getFieldDefinition("category") && (
              <Grid2 size={6}>
                <FormFieldWrapper
                  label={`${
                    getFieldDefinition("category")?.label
                  }${getFieldDefinition("category")?.isMandatory ? "*" : ""}`}
                >
                  <ControlledSelectField
                    options={
                      formState.configuration?.categoryDefinition?.map(
                        (category) => ({
                          label: category.name,
                          value: category.name,
                        })
                      ) ?? []
                    }
                    value={field.category ?? ""}
                    onChange={(val) => {
                      if (onFieldChange) {
                        onFieldChange(field.id, "category", val);
                        return;
                      }
                      const index = getFieldIndexUsingId(field.id);
                      setValue(`projects.${index}.category`, val);

                      const newErrors = {
                        ...(formState.validationErrors ?? {}),
                      } as Record<string, ExtendedFieldError>;
                      if (!val && getFieldDefinition("category")?.isMandatory) {
                        newErrors[`projects.${index}.category`] = {
                          type: "required",
                          message: "This field is required",
                          tabContainingErrors: `Manage ${projectName.plural}`,
                        } as ExtendedFieldError;
                      } else {
                        delete newErrors[`projects.${index}.category`];
                      }
                      setValue("validationErrors", newErrors);
                    }}
                    error={
                      formState.validationErrors?.[
                        `projects.${fieldIndex}.category`
                      ] !== undefined
                    }
                    helperText={
                      formState.validationErrors?.[
                        `projects.${fieldIndex}.category`
                      ]?.message
                    }
                  ></ControlledSelectField>
                </FormFieldWrapper>
              </Grid2>
            )}
            {getFieldDefinition("secondCategory") && (
              <Grid2 size={6}>
                <FormFieldWrapper
                  label={`${
                    getFieldDefinition("secondCategory")?.label
                  }${getFieldDefinition("secondCategory")?.isMandatory ? "*" : ""}`}
                >
                  <ControlledSelectField
                    options={
                      formState.configuration?.secondCategoryDefinition?.map(
                        (category) => ({
                          label: category.name,
                          value: category.name,
                        })
                      ) ?? []
                    }
                    value={field.secondCategory ?? ""}
                    onChange={(val) => {
                      if (onFieldChange) {
                        onFieldChange(field.id, "secondCategory", val);
                        return;
                      }
                      const index = getFieldIndexUsingId(field.id);
                      setValue(`projects.${index}.secondCategory`, val);

                      const newErrors = {
                        ...(formState.validationErrors ?? {}),
                      } as Record<string, ExtendedFieldError>;
                      if (
                        !val &&
                        getFieldDefinition("secondCategory")?.isMandatory
                      ) {
                        newErrors[`projects.${index}.secondCategory`] = {
                          type: "required",
                          message: "This field is required",
                          tabContainingErrors: `Manage ${projectName.plural}`,
                        } as ExtendedFieldError;
                      } else {
                        delete newErrors[`projects.${index}.secondCategory`];
                      }
                      setValue("validationErrors", newErrors);
                    }}
                    error={
                      formState.validationErrors?.[
                        `projects.${fieldIndex}.secondCategory`
                      ] !== undefined
                    }
                    helperText={
                      formState.validationErrors?.[
                        `projects.${fieldIndex}.secondCategory`
                      ]?.message
                    }
                  ></ControlledSelectField>
                </FormFieldWrapper>
              </Grid2>
            )}
          </Grid2>
        </Box>
      </AccordionDetails>
    </Accordion>
  );
};

export default ProjectRow;
